<template>
  <div class="c-data-search-form">
    <c-form ref="searchFormRef" v-bind="searchFormAttrs" :model="searchModel" @keyup="handleKeyup">
      <slot name="search" />
      <c-form-items v-bind="searchItemsAttrs" :model="searchModel">
        <template #operation>
          <el-button
            v-if="$attrs.perm"
            v-perm="$attrs.perm"
            :loading="searchLoading"
            type="primary"
            v-preventReclick
            @click="handleSearch"
          >
            查询
          </el-button>
          <el-button v-else :loading="searchLoading" type="primary" v-preventReclick @click="handleSearch">
            查询
          </el-button>
          <el-button v-preventReclick @click="handleSearchReset">重置</el-button>
        </template>
      </c-form-items>
    </c-form>
  </div>
</template>

<script setup lang="ts">
import {
  _,
  tpl,
  ref,
  computed,
  onMounted,
  useCurrentAppInstance,
  PAGE_SEARCH_EVENT_KEY,
  PAGE_SEARCHED_EVENT_KEY
} from '@zto/zpage'

import type { ApiRequestAction, GenericFunction, AppEventListeners } from '@zto/zpage'

const app = useCurrentAppInstance()

// 属性
const props = withDefaults(
  defineProps<{
    api?: ApiRequestAction | string // api 触发,
    apiParams?: any
    immediate?: boolean
    items?: any[]
    span?: number
    labelWidth?: number
    noOperation?: boolean
    noResetProps?: boolean
    form?: any
    formItems?: any

    listeners?: AppEventListeners

    onReset?: GenericFunction
    onDataLoad?: GenericFunction
  }>(),
  {
    immediate: true
  }
)

// 注册微件事件监听
app.useEventListeners(props.listeners, {
  searchOn: async (e: any) => {
    await doSearch(e?.resetPager, e?.params)
  }
})

// 查询数据模型
const searchModel = app.getPageSearchData()

// 查询表单
const searchFormRef = ref<any>()

// 加载
const searchLoading = ref(false)

const searchFormAttrs = computed(() => {
  return {
    span: props.span || 6,
    items: props.items,
    api: props.api,
    labelWidth: props.labelWidth,
    noResetProps: props.noResetProps,
    ...props.form
  }
})

// 查询表单
const searchItemsAttrs = computed(() => {
  return {
    span: props.span || 6,
    items: props.items,
    api: props.api,
    showOperation: !props.noOperation,
    ...props.formItems
  }
})

onMounted(async () => {
  if (props.immediate) {
    await doSearch()
  }
})

// 按键查询
function handleKeyup(event: KeyboardEvent) {
  if (event.code == 'Enter') doSearch(true)
}

// 执行查询
async function handleSearch() {
  const valid = await searchFormRef.value.validate()
  if (!valid) return

  await doSearch(true)
}

// 执行重设
function handleSearchReset() {
  searchFormRef.value.resetFields(true)

  if (props.onReset) {
    props.onReset(searchModel)
  }
}

// 执行查询
async function doSearch(resetPager = false, refreshPager = false) {
  const context = app.useContext()

  let searchParams: any = {}
  if (props.apiParams) {
    searchParams = tpl.deepFilter(props.apiParams, context)
  }

  searchParams = { ...searchParams, ...searchModel }

  let searchData: any = undefined

  if (props.api) {
    searchLoading.value = true
    searchData = await app.request({ action: props.api, params: searchParams }).finally(() => {
      searchLoading.value = false
    })

    if (props.onDataLoad) {
      const context = app.useContext(searchData)
      await props.onDataLoad(context)
    }

    app.emits(PAGE_SEARCHED_EVENT_KEY, searchData)
  } else {
    // 发送事件
    app.emits(PAGE_SEARCH_EVENT_KEY, {
      resetPager,
      refreshPager,
      data: searchData,
      params: searchParams
    })
  }
}
</script>

<style lang="scss" scoped></style>
